import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import { IDVGItemDTO, IIteratorDTO } from "coreframetypings/dto";
import { IRefreshable } from '#/DefaultViewExtensionInterfaces';
import ComponentManager from '@/Management/ComponentManager';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import ToolBarViewModelBuilder from '@/Builder/ToolBarViewModelBuilder';
import CreateAndConnectLinkViewModel from '@/Components/Links/CreateAndConnectLink/CreateAndConnectLinkViewModel';
import FindAndConnectLinkViewModel from '@/Components/Links/FindAndConnectLink/FindAndConnectLinkViewModel';
import FindAndDisconnectLinkViewModel from '@/Components/Links/FindAndDisconnectLink/FindAndDisconnectLinkViewModel';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import EditContext from '@/ViewModels/Contexts/EditContext';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ToolBarViewModel from '@/ViewModels/Controls/ToolBarViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import Utilities from '@/Utility/Utilities';

export default class ExtIEGAssociationGridViewModel extends ControlViewModel implements IRefreshable {

    protected detailViewContext: DetailViewContext;
    protected editContext: EditContext;
    protected detailViewItem: IDVGItemDTO;
    public toolBar: ToolBarViewModel;
    public label: LabelViewModel;
    public connectedPdoIds: KnockoutObservableArray<string>;
    public connectedObjectIdents: KnockoutObservableArray<string>;

    public constructor(params: any) {
        super(params);

        this.detailViewItem = this.paramOrDefault(params.detailViewItem);
        this.detailViewContext = this.paramOrDefault(params.detailViewContext);
        this.editContext = this.paramOrDefault(params.editContext);
        this.connectedPdoIds = this.observableOrDefault([]) as KnockoutObservableArray<string>;
        this.connectedObjectIdents = this.observableOrDefault([]) as KnockoutObservableArray<string>;
        this.toolBar = this.createToolbar();
        this.label = new LabelViewModelBuilder()
            .withCaption(this.defineComputed(() => {
            return this.connectedObjectIdents().join('; ');
            }))
            .withParentLocator(this.locator)
            .withLocator('connectedObjects')
            .build();
        this.undisposeProperties.push('editContext');
        this.undisposeProperties.push('detailViewContext');
    }

    protected createToolbar(): ToolBarViewModel {
        return new ToolBarViewModelBuilder()
            .withPrimaryLinks(this.createPrimaryLinks())
            .withParentLocator(this.locator)
            .build();
    }

    protected createPrimaryLinks(): LinkViewModel[] {
        return [
            this.createCreateAndConnectLink(),
            this.createFindAndConnectLink(),
            this.createFindAndDisconnectLink()
        ];
    }

    protected createCreateAndConnectLink(): CreateAndConnectLinkViewModel {
        return new CreateAndConnectLinkViewModel({
            parentLocator: LocatorHelper.buildLocator(this.locator, 'toolbar'),
            context: this.detailViewContext,
            listView: this.detailViewItem.listView,
            contextPdoId: this.editContext.genericObject.pdoId,
            contextTypeName: this.editContext.genericObject.pdTypeName,
            isEnabled: this.isEnabled
        });
    }

    protected createFindAndConnectLink(): FindAndConnectLinkViewModel {
        return new FindAndConnectLinkViewModel({
            parentLocator: LocatorHelper.buildLocator(this.locator, 'toolbar'),
            context: this.editContext,
            listView: this.detailViewItem.listView,
            dataSource: this,
            isEnabled: this.isEnabled
        });
    }

    protected createFindAndDisconnectLink(): FindAndDisconnectLinkViewModel {
        return new FindAndDisconnectLinkViewModel({
            parentLocator: LocatorHelper.buildLocator(this.locator, 'toolbar'),
            context: this.editContext,
            listView: this.detailViewItem.listView,
            dataSource: this,
            isEnabled: this.defineComputed(() => {
                const isEnabled = this.isEnabled();
                const hasConnectedPdoIds = this.connectedPdoIds().length > 0;
                return isEnabled && hasConnectedPdoIds;
            })
        });
    }

    public refresh(): void {
        this.increaseBusyCounter();
        RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
            assoziationName: this.detailViewItem.name,
            pdTypeName: this.editContext.genericObject.pdTypeName,
            retrieveExtentCount: true,
            properties: ['ObjectIdentValue'],
            filter: this.detailViewItem.listView.filter,
            itOperator: null,
            orderby: this.detailViewItem.listView.defaultOrder,
            pdo_id: this.editContext.genericObject.pdoId
        }).then((iterator: IIteratorDTO) => {
            const count = parseInt(iterator.extentCount);
            if (count > 0) {
                return RADEnvironmentProxy.getInstance().getExtentWithPDOIDAsFirstColumn({
                    iteratorID: iterator.id,
                    start: 0,
                    end: count
                }).then((rows: any[][]) => {
                    const pdoIds = rows.map(row => row[0]);
                    this.connectedPdoIds(pdoIds);
                    const objectIdents = rows.map(row => row[1]);
                    this.connectedObjectIdents(objectIdents);
                });
            }
            else {
                this.connectedPdoIds([]);
                this.connectedObjectIdents([]);
            }
            return Utilities.resolveImmediately();
        }).always(() => {
            this.decreaseBusyCounter();
        });
    }
}

ComponentManager.setViewModel('ExtIEGAssociationGrid', ExtIEGAssociationGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');